import { Link } from 'react-router-dom';
import type { Section, Subsection } from '../../config/types';
import type { FetchFormResponse } from '../../service/types';
import { Status } from '../../types';
import {
  getIncompleteRequiredSubsections,
  getLockedSections,
  getLockedSubsections,
  getOverviewStatus,
} from '../../utils/config-utils';

type Entry = ReturnType<typeof getIncompleteRequiredSubsections>[number];

type Props = {
  sections: ReturnType<typeof getIncompleteRequiredSubsections>;
  allSections: Section[];
  subsectionMap: Record<string, Subsection>;
  formValues?: FetchFormResponse;
  tenant?: string;
  formId?: string;
};

export default function IncompleteSections({
  sections,
  allSections,
  subsectionMap,
  formValues,
  tenant,
  formId,
}: Props) {
  if (!sections.length) return null;
  const base = tenant ? `/${tenant}` : '';
  const search = formId ? `?formId=${encodeURIComponent(formId)}` : '';
  const statuses = getOverviewStatus(allSections, subsectionMap, formValues);
  const lockedSections = getLockedSections(allSections, subsectionMap, formValues);
  const lockedSubsections = getLockedSubsections(allSections, subsectionMap, formValues);
  const incomplete = getIncompleteRequiredSubsections(allSections, statuses, true).filter(
    ({ subsection }) => statuses[subsection.slug] !== Status.NotDue,
  );
  const isLocked = ({ section, subsection }: Entry) =>
    lockedSections[section.slug] || lockedSubsections[subsection.slug];
  const href = ({ section, subsection }: Entry) =>
    `${base}/${section.slug}/${subsection.slug}${search}`;
  const getPrerequisites = (entry: Entry, visited = new Set<string>()): Entry[] => {
    if (visited.has(entry.subsection.slug)) return [];
    if (!isLocked(entry)) return [entry];
    const nextVisited = new Set(visited).add(entry.subsection.slug);
    const dependencies = [entry.subsection.dependsOn || []].flat();
    return incomplete
      .filter(
        ({ section, subsection }) =>
          section.slug === entry.section.dependsOn || dependencies.includes(subsection.slug),
      )
      .flatMap((prerequisite) => getPrerequisites(prerequisite, nextVisited));
  };
  return (
    <div className="govuk-!-margin-top-4">
      <p className="govuk-body-s">The below sections are incomplete</p>
      <ul className="govuk-list govuk-!-font-size-16">
        {sections.map((entry) => {
          const { section, subsection } = entry;
          const locked = isLocked(entry);
          const prerequisites = locked
            ? [
                ...new Map(
                  getPrerequisites(entry).map((item) => [item.subsection.slug, item]),
                ).values(),
              ]
            : [];
          return (
            <li key={`${section.slug}/${subsection.slug}`}>
              {subsection.title}{' '}
              {locked ? (
                prerequisites.length ? (
                  <>
                    (Requires{' '}
                    {prerequisites.map((prerequisite, index) => (
                      <span key={prerequisite.subsection.slug}>
                        {index > 0 && ', '}
                        <Link className="govuk-link" to={href(prerequisite)}>
                          {prerequisite.subsection.title}
                        </Link>
                      </span>
                    ))}
                    )
                  </>
                ) : (
                  '(Locked)'
                )
              ) : (
                <Link className="govuk-link" to={href(entry)}>
                  Go to section
                  <span className="govuk-visually-hidden"> for {subsection.title}</span>
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
